import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Typography,
  Checkbox,
  Form,
  Space,
  message,
  Row,
  Col,
  Divider,
  Progress,
  Tag,
  Empty,
  Modal,
  Input,
  InputNumber,
  Rate,
  Select,
  DatePicker,
  Table,
  Calendar,
  Timeline,
  Segmented,
  Badge
} from 'antd';
import {
  HeartOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  FireOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  CalendarOutlined,
  BarChartOutlined,
  StarOutlined,
  ClockCircleOutlined,
  BulbOutlined,
  InfoCircleOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  EditOutlined,
  FlagOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { healthConfig } from '../config/health';
import { useAuthStore } from '../stores/authStore';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { Title, Text } = Typography;
const BRAND = '#6366F1';

// 打卡分类定义（与后端保持一致）
const CATEGORY_META = {
  diet: { name: '饮食', icon: '🍎', color: '#FF6B6B' },
  exercise: { name: '运动', icon: '🏃', color: '#4ECDC4' },
  sleep: { name: '睡眠', icon: '😴', color: '#45B7D1' },
  mental: { name: '心理', icon: '🧘', color: '#96CEB4' },
  medical: { name: '医疗', icon: '🏥', color: '#F0A500' }
};

const Health = () => {
  const navigate = useNavigate();
  const { initialize } = useAuthStore();
  const [checkins, setCheckins] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCheckinModal, setShowCheckinModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [checkinData, setCheckinData] = useState({});
  const [form] = Form.useForm();
  const [showTipsModal, setShowTipsModal] = useState(false);

  // 视图与筛选（客户端过滤，保证月历数据完整）
  const [viewMode, setViewMode] = useState('calendar');
  const [filterMonth, setFilterMonth] = useState(null);
  const [onlyFlagged, setOnlyFlagged] = useState(false);
  const [sortOrder, setSortOrder] = useState('date_desc');

  // 加载打卡数据（保持原有无参调用）
  const fetchCheckins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(healthConfig.apiEndpoints.checkins);
      if (response.data.success) {
        setCheckins(response.data.checkins || []);
        const checkinMap = {};
        (response.data.checkins || []).forEach(checkin => {
          checkinMap[checkin.checkin_date] = checkin;
        });
        setCheckinData(checkinMap);
      } else {
        message.error(response.data.message || '获取打卡数据失败');
      }
    } catch (error) {
      console.error('获取打卡数据失败:', error);
      message.error('获取打卡数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 加载打卡总览统计（新接口）
  const fetchOverview = async () => {
    try {
      const response = await axios.get('/health/checkins/stats/overview');
      if (response.data.success) {
        setOverview(response.data.overview);
      }
    } catch (error) {
      console.error('获取打卡总览失败:', error);
    }
  };

  // 打开打卡弹窗
  const handleOpenCheckin = (date) => {
    setSelectedDate(date);
    const dateStr = date.format('YYYY-MM-DD');
    const existingCheckin = checkinData[dateStr];

    if (existingCheckin) {
      form.setFieldsValue({
        // 饮食类
        vegetables_fruits: existingCheckin.vegetables_fruits === true,
        oil_salt_sugar: existingCheckin.oil_salt_sugar === true,
        protein: existingCheckin.protein === true,
        portion_control: existingCheckin.portion_control === true,
        // 运动类
        cardio: existingCheckin.cardio === true,
        strength_training: existingCheckin.strength_training === true,
        reduce_sitting: existingCheckin.reduce_sitting === true,
        // 睡眠类
        sleep_hours: existingCheckin.sleep_hours === true,
        early_bed: existingCheckin.early_bed === true,
        bedtime_habits: existingCheckin.bedtime_habits === true,
        // 心理健康类
        relaxation: existingCheckin.relaxation === true,
        emotion_expression: existingCheckin.emotion_expression === true,
        social_interaction: existingCheckin.social_interaction === true,
        // 医疗管理类
        medication: existingCheckin.medication === true,
        health_checkup: existingCheckin.health_checkup === true,
        risk_avoidance: existingCheckin.risk_avoidance === true,
        // 新增指标
        weight: existingCheckin.weight != null ? Number(existingCheckin.weight) : undefined,
        mood_score: existingCheckin.mood_score || undefined,
        sleep_quality: existingCheckin.sleep_quality || undefined,
        water_cups: existingCheckin.water_cups != null ? existingCheckin.water_cups : undefined,
        steps: existingCheckin.steps != null ? existingCheckin.steps : undefined,
        notes: existingCheckin.notes || undefined,
        is_flagged: existingCheckin.is_flagged === true
      });
    } else {
      form.resetFields();
    }
    setShowCheckinModal(true);
  };

  // 保存打卡数据
  const handleSaveCheckin = async (values) => {
    try {
      const dateStr = selectedDate.format('YYYY-MM-DD');
      const existingCheckin = checkinData[dateStr];

      let response;
      const requestBody = {
        checkin_date: dateStr,
        // 饮食类
        vegetables_fruits: values.vegetables_fruits || false,
        oil_salt_sugar: values.oil_salt_sugar || false,
        protein: values.protein || false,
        portion_control: values.portion_control || false,
        // 运动类
        cardio: values.cardio || false,
        strength_training: values.strength_training || false,
        reduce_sitting: values.reduce_sitting || false,
        // 睡眠类
        sleep_hours: values.sleep_hours || false,
        early_bed: values.early_bed || false,
        bedtime_habits: values.bedtime_habits || false,
        // 心理健康类
        relaxation: values.relaxation || false,
        emotion_expression: values.emotion_expression || false,
        social_interaction: values.social_interaction || false,
        // 医疗管理类
        medication: values.medication || false,
        health_checkup: values.health_checkup || false,
        risk_avoidance: values.risk_avoidance || false,
        // 新增指标
        weight: values.weight ?? null,
        mood_score: values.mood_score ?? null,
        sleep_quality: values.sleep_quality ?? null,
        water_cups: values.water_cups ?? null,
        steps: values.steps ?? null,
        notes: values.notes ?? null,
        is_flagged: values.is_flagged || false
      };

      if (existingCheckin) {
        response = await axios.put(`${healthConfig.apiEndpoints.update}/${existingCheckin.id}`, requestBody);
      } else {
        response = await axios.post(healthConfig.apiEndpoints.create, requestBody);
      }

      if (response.data.success) {
        message.success(existingCheckin ? '打卡更新成功' : '打卡成功');
        setShowCheckinModal(false);
        form.resetFields();
        fetchCheckins();
        fetchOverview();
      } else {
        message.error(response.data.message || '打卡失败');
      }
    } catch (error) {
      console.error('保存打卡失败:', error);
      message.error('操作失败');
    }
  };

  // 计算打卡完成率
  const calculateCompletionRate = (checkin) => {
    if (!checkin) return 0;
    if (typeof checkin.completion_rate === 'number') return checkin.completion_rate;
    const fields = [
      'vegetables_fruits', 'oil_salt_sugar', 'protein', 'portion_control',
      'cardio', 'strength_training', 'reduce_sitting',
      'sleep_hours', 'early_bed', 'bedtime_habits',
      'relaxation', 'emotion_expression', 'social_interaction',
      'medication', 'health_checkup', 'risk_avoidance'
    ];
    let completedItems = 0;
    fields.forEach(field => {
      if (checkin[field]) completedItems++;
    });
    return Math.round((completedItems / fields.length) * 100);
  };

  // 获取打卡状态
  const getCheckinStatus = (date) => {
    const dateStr = date.format('YYYY-MM-DD');
    const checkin = checkinData[dateStr];
    if (!checkin) return 'none';
    const completionRate = calculateCompletionRate(checkin);
    if (completionRate >= 80) return 'excellent';
    if (completionRate >= 60) return 'good';
    if (completionRate >= 40) return 'fair';
    return 'poor';
  };

  // 获取打卡状态颜色
  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return '#52C41A';
      case 'good': return '#1890FF';
      case 'fair': return '#FAAD14';
      case 'poor': return '#FF4D4F';
      default: return '#D9D9D9';
    }
  };

  // 月历单元格渲染
  const dateCellRender = (date) => {
    const status = getCheckinStatus(date);
    if (status === 'none') return null;
    return (
      <div style={{
        position: 'absolute',
        top: '2px',
        right: '2px',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        backgroundColor: getStatusColor(status)
      }} />
    );
  };

  // 分类完成情况
  const getCategoryStat = (checkin, key) => {
    const groups = {
      diet: ['vegetables_fruits', 'oil_salt_sugar', 'protein', 'portion_control'],
      exercise: ['cardio', 'strength_training', 'reduce_sitting'],
      sleep: ['sleep_hours', 'early_bed', 'bedtime_habits'],
      mental: ['relaxation', 'emotion_expression', 'social_interaction'],
      medical: ['medication', 'health_checkup', 'risk_avoidance']
    };
    const fields = groups[key] || [];
    const done = fields.reduce((n, f) => n + (checkin[f] ? 1 : 0), 0);
    return { done, total: fields.length };
  };

  // 客户端过滤 + 排序
  const filteredCheckins = useMemo(() => {
    let list = [...checkins];
    if (filterMonth) {
      const mp = filterMonth.format('YYYY-MM');
      list = list.filter(c => (c.checkin_date || '').startsWith(mp));
    }
    if (onlyFlagged) {
      list = list.filter(c => c.is_flagged === true);
    }
    list.sort((a, b) => {
      const da = a.checkin_date || '';
      const db = b.checkin_date || '';
      return sortOrder === 'date_asc' ? da.localeCompare(db) : db.localeCompare(da);
    });
    return list;
  }, [checkins, filterMonth, onlyFlagged, sortOrder]);

  // 组件加载时获取数据
  useEffect(() => {
    initialize();
    fetchCheckins();
    fetchOverview();
  }, [initialize]);

  const stats = overview || { streak: 0, thisWeek: 0, thisMonth: 0, avgCompletion: 0, flaggedCount: 0, total: 0, byCategory: [] };

  // 表格列
  const columns = [
    {
      title: '日期',
      dataIndex: 'checkin_date',
      key: 'checkin_date',
      render: (d, r) => (
        <Space>
          <Text strong>{d}</Text>
          {r.is_flagged && <Tag color="red" icon={<FlagOutlined />}>提醒</Tag>}
        </Space>
      )
    },
    {
      title: '完成率',
      dataIndex: 'completion_rate',
      key: 'completion_rate',
      render: (_, r) => {
        const rate = calculateCompletionRate(r);
        return <Progress percent={rate} size="small" strokeColor={getStatusColor(rate >= 80 ? 'excellent' : rate >= 60 ? 'good' : rate >= 40 ? 'fair' : 'poor')} style={{ maxWidth: 160 }} />;
      }
    },
    {
      title: '分类',
      key: 'categories',
      render: (_, r) => (
        <div className="mod-badges">
          {Object.keys(CATEGORY_META).map(k => {
            const s = getCategoryStat(r, k);
            return <span key={k} className="mod-badge" style={{ color: CATEGORY_META[k].color }}>{CATEGORY_META[k].icon}{s.done}/{s.total}</span>;
          })}
        </div>
      )
    },
    {
      title: '体征',
      key: 'metrics',
      render: (_, r) => (
        <Space size={4} wrap>
          {r.weight != null && <Tag>体重 {Number(r.weight)}kg</Tag>}
          {r.mood_score != null && <Tag color="magenta">心情 {r.mood_score}★</Tag>}
          {r.steps != null && <Tag color="cyan">{r.steps}步</Tag>}
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      render: (_, r) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => handleOpenCheckin(dayjs(r.checkin_date))}>编辑</Button>
      )
    }
  ];

  const renderCheckinCards = () => {
    if (filteredCheckins.length === 0) {
      return <Empty description="暂无打卡记录" style={{ padding: '40px 0' }} />;
    }
    return (
      <Row gutter={[16, 16]}>
        {filteredCheckins.map(checkin => {
          const rate = calculateCompletionRate(checkin);
          const statusColor = getStatusColor(rate >= 80 ? 'excellent' : rate >= 60 ? 'good' : rate >= 40 ? 'fair' : 'poor');
          return (
            <Col xs={24} sm={12} lg={8} xl={6} key={checkin.id}>
              <div className="mod-card">
                <div className="mod-card-head">
                  <div className="mod-card-icon" style={{ background: statusColor }}>
                    <CalendarOutlined />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="mod-card-title">{checkin.checkin_date}</div>
                    <div className="mod-card-sub">完成 {rate}%</div>
                  </div>
                  {checkin.is_flagged && <Tag color="red" icon={<FlagOutlined />} style={{ margin: 0 }}>提醒</Tag>}
                </div>
                <div className="mod-card-body">
                  <Progress percent={rate} strokeColor={statusColor} size="small" style={{ marginBottom: 12 }} />
                  <div className="mod-badges" style={{ marginBottom: 8 }}>
                    {Object.keys(CATEGORY_META).map(k => {
                      const s = getCategoryStat(checkin, k);
                      const full = s.done === s.total;
                      return (
                        <span key={k} className={`mod-badge ${full ? 'mod-badge-success' : ''}`} style={!full ? { color: CATEGORY_META[k].color } : {}}>
                          {CATEGORY_META[k].icon} {s.done}/{s.total}
                        </span>
                      );
                    })}
                  </div>
                  <Space size={4} wrap>
                    {checkin.weight != null && <Tag>体重 {Number(checkin.weight)}kg</Tag>}
                    {checkin.mood_score != null && <Tag color="magenta">心情 {checkin.mood_score}★</Tag>}
                    {checkin.sleep_quality != null && <Tag color="blue">睡眠 {checkin.sleep_quality}★</Tag>}
                    {checkin.water_cups != null && <Tag color="geekblue">{checkin.water_cups}杯水</Tag>}
                    {checkin.steps != null && <Tag color="cyan">{checkin.steps}步</Tag>}
                  </Space>
                  {checkin.notes && <div style={{ marginTop: 8, color: '#64748b', fontSize: 12 }}>{checkin.notes}</div>}
                </div>
                <div className="mod-card-actions">
                  <Button size="small" type="text" icon={<EditOutlined />} onClick={() => handleOpenCheckin(dayjs(checkin.checkin_date))}>编辑</Button>
                </div>
              </div>
            </Col>
          );
        })}
      </Row>
    );
  };

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">
            <HeartOutlined style={{ marginRight: 10 }} />
            {healthConfig.name}
          </div>
          <div className="mod-header-subtitle">打卡 · 监测 · 预防 — 记录每日健康习惯，追踪连续打卡与指标趋势</div>
        </div>
        <Space wrap>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')}>返回</Button>
          <Button icon={<InfoCircleOutlined />} onClick={() => setShowTipsModal(true)}>健康TIPS</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => handleOpenCheckin(dayjs())} style={{ background: BRAND, borderColor: BRAND }}>
            今日打卡
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#f97316,#fb923c)' }}><FireOutlined /></div>
          <div>
            <div className="mod-stat-value">{stats.streak}</div>
            <div className="mod-stat-label">连续打卡(天)</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#6366f1,#818cf8)' }}><CalendarOutlined /></div>
          <div>
            <div className="mod-stat-value">{stats.thisWeek}</div>
            <div className="mod-stat-label">本周打卡</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#0ea5e9,#38bdf8)' }}><BarChartOutlined /></div>
          <div>
            <div className="mod-stat-value">{stats.thisMonth}</div>
            <div className="mod-stat-label">本月打卡</div>
          </div>
        </div>
        <div className="mod-stat-card">
          <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#22c55e,#4ade80)' }}><CheckCircleOutlined /></div>
          <div>
            <div className="mod-stat-value">{stats.avgCompletion}%</div>
            <div className="mod-stat-label">平均完成率</div>
          </div>
        </div>
        <div
          className={`mod-stat-card clickable ${stats.flaggedCount > 0 ? 'danger' : ''}`}
          onClick={() => { setOnlyFlagged(v => !v); if (viewMode === 'calendar') setViewMode('card'); }}
        >
          <div className="mod-stat-icon" style={{ background: 'linear-gradient(135deg,#ef4444,#f87171)' }}><FlagOutlined /></div>
          <div>
            <div className="mod-stat-value">{stats.flaggedCount}</div>
            <div className="mod-stat-label">异常/提醒{onlyFlagged ? ' (筛选中)' : ''}</div>
          </div>
        </div>
      </div>

      {/* 分类完成率概览 */}
      {stats.byCategory && stats.byCategory.length > 0 && (
        <Card style={{ borderRadius: 16, marginBottom: 16 }} bodyStyle={{ padding: '16px 20px' }}>
          <div className="mod-section-title"><ThunderboltOutlined style={{ marginRight: 6 }} />五大维度完成率</div>
          <Row gutter={[16, 12]}>
            {stats.byCategory.map(c => (
              <Col xs={12} md={8} lg={Math.floor(24 / 5) || 4} key={c.key} style={{ textAlign: 'center' }}>
                <Progress type="circle" percent={c.rate} size={72} strokeColor={CATEGORY_META[c.key]?.color || BRAND} />
                <div style={{ marginTop: 6, fontSize: 13 }}>{CATEGORY_META[c.key]?.icon} {CATEGORY_META[c.key]?.name}</div>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      {/* 工具栏 */}
      <Card className="mod-toolbar" bodyStyle={{ padding: 16 }}>
        <div className="mod-toolbar-row" style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <Segmented
            value={viewMode}
            onChange={setViewMode}
            options={[
              { label: '月历', value: 'calendar', icon: <CalendarOutlined /> },
              { label: '卡片', value: 'card', icon: <AppstoreOutlined /> },
              { label: '表格', value: 'table', icon: <UnorderedListOutlined /> }
            ]}
          />
          <DatePicker
            picker="month"
            placeholder="按月份筛选"
            value={filterMonth}
            onChange={setFilterMonth}
            allowClear
          />
          <Select
            value={sortOrder}
            onChange={setSortOrder}
            style={{ width: 140 }}
            options={[
              { label: '日期最新', value: 'date_desc' },
              { label: '日期最早', value: 'date_asc' }
            ]}
          />
          <Button
            type={onlyFlagged ? 'primary' : 'default'}
            danger={onlyFlagged}
            icon={<FlagOutlined />}
            onClick={() => setOnlyFlagged(v => !v)}
          >
            仅看提醒
          </Button>
          <div style={{ flex: 1 }} />
          <Text type="secondary">共 {filteredCheckins.length} 条记录</Text>
        </div>
      </Card>

      {/* 内容视图 */}
      {viewMode === 'calendar' && (
        <Card style={{ borderRadius: 16, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }} bodyStyle={{ padding: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <Space size={16} wrap>
              <span><Badge color="#52C41A" text="优秀 ≥80%" /></span>
              <span><Badge color="#1890FF" text="良好 ≥60%" /></span>
              <span><Badge color="#FAAD14" text="一般 ≥40%" /></span>
              <span><Badge color="#FF4D4F" text="待改进" /></span>
            </Space>
          </div>
          <Calendar
            cellRender={dateCellRender}
            onSelect={handleOpenCheckin}
            style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: 12 }}
          />
        </Card>
      )}
      {viewMode === 'card' && renderCheckinCards()}
      {viewMode === 'table' && (
        <Card style={{ borderRadius: 16 }} bodyStyle={{ padding: 0 }}>
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={filteredCheckins}
            pagination={{ pageSize: 10, hideOnSinglePage: true }}
            scroll={{ x: 720 }}
            locale={{ emptyText: <Empty description="暂无打卡记录" /> }}
          />
        </Card>
      )}

      {/* 健康打卡弹窗 */}
      <Modal
        open={showCheckinModal}
        onCancel={() => setShowCheckinModal(false)}
        onOk={() => form.submit()}
        okText="保存打卡"
        cancelText="取消"
        width={680}
        title={<span><HeartOutlined style={{ color: BRAND, marginRight: 8 }} />健康打卡 · {selectedDate.format('YYYY年MM月DD日')}</span>}
        styles={{ body: { maxHeight: '70vh', overflow: 'auto' } }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSaveCheckin}>
          {/* 饮食管理 */}
          <div className="mod-section-title" style={{ color: CATEGORY_META.diet.color }}>🍎 饮食管理</div>
          <Divider style={{ margin: '8px 0' }} />
          <Form.Item name="vegetables_fruits" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>今天是否吃够了3种以上蔬菜、2种水果?</Checkbox>
          </Form.Item>
          <Form.Item name="oil_salt_sugar" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否控制了油盐糖? (少油、少盐、少饮料/甜食)</Checkbox>
          </Form.Item>
          <Form.Item name="protein" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否吃了优质蛋白? (鸡蛋、鱼、瘦肉、豆制品、牛奶)</Checkbox>
          </Form.Item>
          <Form.Item name="portion_control" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否避免暴饮暴食,做到七八分饱?</Checkbox>
          </Form.Item>

          {/* 运动管理 */}
          <div className="mod-section-title" style={{ color: CATEGORY_META.exercise.color }}>🏃 运动管理</div>
          <Divider style={{ margin: '8px 0' }} />
          <Form.Item name="cardio" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>今天有没有进行≥30分钟的中等强度运动? (快走、慢跑、骑车等)</Checkbox>
          </Form.Item>
          <Form.Item name="strength_training" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>一周是否完成了≥2次力量训练? (深蹲、俯卧撑、哑铃等)</Checkbox>
          </Form.Item>
          <Form.Item name="reduce_sitting" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否减少久坐,每隔1小时起来活动一下?</Checkbox>
          </Form.Item>

          {/* 睡眠管理 */}
          <div className="mod-section-title" style={{ color: CATEGORY_META.sleep.color }}>😴 睡眠管理</div>
          <Divider style={{ margin: '8px 0' }} />
          <Form.Item name="sleep_hours" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否保证了7-9小时睡眠?</Checkbox>
          </Form.Item>
          <Form.Item name="early_bed" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否在23点前上床,避免熬夜?</Checkbox>
          </Form.Item>
          <Form.Item name="bedtime_habits" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否在睡前减少刷手机/饮咖啡/喝酒?</Checkbox>
          </Form.Item>

          {/* 心理健康 */}
          <div className="mod-section-title" style={{ color: CATEGORY_META.mental.color }}>🧘 心理健康</div>
          <Divider style={{ margin: '8px 0' }} />
          <Form.Item name="relaxation" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>今天是否进行了放松活动? (冥想、深呼吸、听音乐等)</Checkbox>
          </Form.Item>
          <Form.Item name="emotion_expression" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否及时表达和调节情绪?</Checkbox>
          </Form.Item>
          <Form.Item name="social_interaction" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否与家人朋友保持良好互动?</Checkbox>
          </Form.Item>

          {/* 医疗管理 */}
          <div className="mod-section-title" style={{ color: CATEGORY_META.medical.color }}>🏥 医疗管理</div>
          <Divider style={{ margin: '8px 0' }} />
          <Form.Item name="medication" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否按时服药(如有)?</Checkbox>
          </Form.Item>
          <Form.Item name="health_checkup" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否定期体检?</Checkbox>
          </Form.Item>
          <Form.Item name="risk_avoidance" valuePropName="checked" style={{ marginBottom: 8 }}>
            <Checkbox>是否避免健康风险行为? (戒烟、限酒、安全驾驶等)</Checkbox>
          </Form.Item>

          {/* 健康指标 */}
          <div className="mod-section-title" style={{ color: BRAND }}><BarChartOutlined style={{ marginRight: 6 }} />健康指标（可选）</div>
          <Divider style={{ margin: '8px 0' }} />
          <Row gutter={16}>
            <Col xs={12} sm={8}>
              <Form.Item name="weight" label="体重 (kg)">
                <InputNumber min={0} max={500} step={0.1} style={{ width: '100%' }} placeholder="如 65.5" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="water_cups" label="饮水 (杯)">
                <InputNumber min={0} max={30} style={{ width: '100%' }} placeholder="如 8" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="steps" label="步数">
                <InputNumber min={0} max={100000} style={{ width: '100%' }} placeholder="如 8000" />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="mood_score" label="心情">
                <Rate />
              </Form.Item>
            </Col>
            <Col xs={12} sm={8}>
              <Form.Item name="sleep_quality" label="睡眠质量">
                <Rate />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item name="is_flagged" valuePropName="checked" label="标记">
                <Checkbox>异常/需提醒</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="notes" label="备注 / 症状">
            <Input.TextArea rows={2} placeholder="记录今日身体状况、异常症状或提醒事项" maxLength={500} showCount />
          </Form.Item>
        </Form>
      </Modal>

      {/* 健康TIPS弹窗 */}
      <Modal
        open={showTipsModal}
        onCancel={() => setShowTipsModal(false)}
        footer={null}
        width={840}
        title={<span><BulbOutlined style={{ color: BRAND, marginRight: 8 }} />健康管理TIPS</span>}
        styles={{ body: { maxHeight: '72vh', overflow: 'auto' } }}
      >
        <Card title="科学的健康管理核心理念" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#52C41A' }}><CheckCircleOutlined style={{ marginRight: 8 }} />以预防为主</Title>
              <Text type="secondary">早筛查、早发现、早干预</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>定期体检，及时发现健康问题</li>
                <li>建立健康档案，跟踪健康趋势</li>
                <li>预防胜于治疗，防患于未然</li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#1890FF' }}><BarChartOutlined style={{ marginRight: 8 }} />数据化管理</Title>
              <Text type="secondary">通过数据跟踪趋势，发现风险点</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>记录健康数据，建立个人健康档案</li>
                <li>分析健康趋势，识别潜在风险</li>
                <li>制定个性化健康管理方案</li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#FAAD14' }}><StarOutlined style={{ marginRight: 8 }} />全方位管理</Title>
              <Text type="secondary">身体、心理、生活习惯、环境影响</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>身体健康的全面监测</li>
                <li>心理健康的关注和维护</li>
                <li>生活习惯的改善和优化</li>
              </ul>
            </Col>
            <Col xs={24} md={12}>
              <Title level={5} style={{ color: '#FF6B6B' }}><HeartOutlined style={{ marginRight: 8 }} />个性化管理</Title>
              <Text type="secondary">根据个人情况制定不同方案</Text>
              <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                <li>考虑年龄、性别、遗传背景</li>
                <li>结合个人生活习惯和偏好</li>
                <li>制定适合的健康管理计划</li>
              </ul>
            </Col>
          </Row>
        </Card>

        <Card title="科学健康管理的五大要素" style={{ marginBottom: 16 }}>
          <Timeline
            items={[
              { color: 'green', children: (<><Title level={5}>🍎 饮食管理</Title><Text type="secondary">均衡饮食，控制总热量</Text><ul style={{ marginTop: 8, paddingLeft: 20 }}><li>碳水、蛋白、脂肪、维生素、矿物质合理搭配</li><li>控制总热量，少油少盐少糖</li><li>多蔬菜水果，少加工食品</li></ul></>) },
              { color: 'blue', children: (<><Title level={5}>🏃 运动管理</Title><Text type="secondary">每周≥150分钟中等强度有氧运动</Text><ul style={{ marginTop: 8, paddingLeft: 20 }}><li>每周≥150分钟中等强度有氧运动（快走、游泳、骑车）</li><li>每周≥2次力量训练（维持肌肉和骨骼）</li><li>避免久坐，增加日常活动量</li></ul></>) },
              { color: 'purple', children: (<><Title level={5}>😴 睡眠管理</Title><Text type="secondary">成年人建议每天7-9小时高质量睡眠</Text><ul style={{ marginTop: 8, paddingLeft: 20 }}><li>成年人建议每天7-9小时高质量睡眠</li><li>规律作息，避免熬夜</li><li>创造良好的睡眠环境</li></ul></>) },
              { color: 'orange', children: (<><Title level={5}>🧘 心理管理</Title><Text type="secondary">学会情绪调节，保持良好人际关系</Text><ul style={{ marginTop: 8, paddingLeft: 20 }}><li>学会情绪调节（冥想、运动、倾诉、写日记）</li><li>保持良好人际关系</li><li>避免长期压力，学会放松</li></ul></>) },
              { color: 'red', children: (<><Title level={5}>🏥 医疗与风险管理</Title><Text type="secondary">定期体检，科学用药</Text><ul style={{ marginTop: 8, paddingLeft: 20 }}><li>定期体检（血压、血糖、血脂、肿瘤筛查）</li><li>有慢性病要科学用药，长期监测</li><li>接种必要的疫苗</li></ul></>) }
            ]}
          />
        </Card>

        <Card title="健康管理建议">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Title level={5} style={{ color: '#52C41A' }}><CheckCircleOutlined style={{ marginRight: 8 }} />日常健康习惯</Title>
              <ul style={{ paddingLeft: 20 }}>
                <li>每天喝8杯水，保持身体水分</li>
                <li>多吃蔬菜水果，补充维生素</li>
                <li>规律作息，保证充足睡眠</li>
                <li>适量运动，增强体质</li>
                <li>保持心情愉悦，减少压力</li>
              </ul>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ color: '#1890FF' }}><ExclamationCircleOutlined style={{ marginRight: 8 }} />预防保健</Title>
              <ul style={{ paddingLeft: 20 }}>
                <li>定期体检，早发现早治疗</li>
                <li>接种疫苗，预防传染病</li>
                <li>注意个人卫生，预防疾病</li>
                <li>避免不良习惯，保护健康</li>
                <li>学习健康知识，提高健康素养</li>
              </ul>
            </Col>
            <Col xs={24} md={8}>
              <Title level={5} style={{ color: '#FAAD14' }}><ClockCircleOutlined style={{ marginRight: 8 }} />慢性病管理</Title>
              <ul style={{ paddingLeft: 20 }}>
                <li>遵医嘱用药，定期复查</li>
                <li>控制饮食，合理运动</li>
                <li>监测指标，及时调整</li>
                <li>保持良好心态，积极配合治疗</li>
                <li>建立健康档案，跟踪病情变化</li>
              </ul>
            </Col>
          </Row>
        </Card>
      </Modal>
    </div>
  );
};

export default Health;
